import { useEffect, useState } from "react";
import { apiFetch } from "@/lib/api/client";
import type { InstructionSummarizeKind } from "@/lib/instruction-limit";
import {
  uploadReadiness,
  type ReadySummaryJob,
  type UploadJobStatus,
} from "@/lib/reference-summary-file";

export interface SummarizeJob extends ReadySummaryJob {
  scope: "custom-role" | "contract-type";
  ownerId: string;
  kind: InstructionSummarizeKind;
  status: UploadJobStatus;
  error?: string;
  serverJobId: string;
}

interface StartSummarizeJobInput {
  key: string;
  scope: SummarizeJob["scope"];
  ownerId: string;
  fileName: string;
  kind: InstructionSummarizeKind;
  text: string;
  savedDocumentId?: string;
}

const jobs = new Map<string, SummarizeJob>();
const controllers = new Map<string, AbortController>();
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

export function subscribeSummarizeJobs(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function listSummarizeJobs(scope?: SummarizeJob["scope"], ownerId?: string): SummarizeJob[] {
  return [...jobs.values()].filter((job) => {
    if (scope && job.scope !== scope) return false;
    if (ownerId && job.ownerId !== ownerId) return false;
    return true;
  });
}

export function uploadStatusFor(
  scope: SummarizeJob["scope"],
  ownerId: string
): "ready" | "not-ready" | null {
  return uploadReadiness(listSummarizeJobs(scope, ownerId).map((job) => job.status));
}

export function startSummarizeJob(input: StartSummarizeJobInput): void {
  const previous = jobs.get(input.key);
  if (previous) void deleteServerSummarize(previous.serverJobId);
  controllers.get(input.key)?.abort();
  const controller = new AbortController();
  const serverJobId = crypto.randomUUID();
  controllers.set(input.key, controller);
  jobs.set(input.key, {
    key: input.key,
    scope: input.scope,
    ownerId: input.ownerId,
    fileName: input.fileName,
    kind: input.kind,
    status: "summarizing",
    extractedText: input.text,
    savedDocumentId: input.savedDocumentId,
    serverJobId,
  });
  emit();
  void runSummarizeJob(input, controller, serverJobId);
}

async function runSummarizeJob(
  input: StartSummarizeJobInput,
  controller: AbortController,
  serverJobId: string
): Promise<void> {
  try {
    const result = await apiFetch<{ summary: string }>("/api/instructions/summarize", {
      method: "POST",
      body: JSON.stringify({ text: input.text, kind: input.kind, jobId: serverJobId }),
      signal: controller.signal,
    });
    if (controller.signal.aborted || !jobs.has(input.key)) return;
    const current = jobs.get(input.key);
    if (!current) return;
    if (typeof result.summary !== "string" || !result.summary.trim()) {
      jobs.set(input.key, {
        ...current,
        status: "failed",
        error: "Summarizer returned an empty brief.",
      });
      emit();
      return;
    }
    jobs.set(input.key, {
      ...current,
      status: "ready",
      extractedText: result.summary,
      error: undefined,
    });
    emit();
  } catch (reason: unknown) {
    if (controller.signal.aborted || isAbortError(reason) || !jobs.has(input.key)) return;
    const current = jobs.get(input.key);
    if (!current) return;
    jobs.set(input.key, {
      ...current,
      status: "failed",
      error: reason instanceof Error ? reason.message : "Could not summarize instructions.",
    });
    emit();
  } finally {
    if (controllers.get(input.key) === controller) controllers.delete(input.key);
  }
}

/** Stops the in-flight model request and drops the job. */
export function cancelSummarizeJob(key: string): void {
  const job = jobs.get(key);
  controllers.get(key)?.abort();
  controllers.delete(key);
  if (job) {
    jobs.delete(key);
    emit();
    void deleteServerSummarize(job.serverJobId);
  }
}

export function deleteServerSummarize(jobId: string): Promise<void> {
  return apiFetch(`/api/instructions/summarize/${jobId}`, { method: "DELETE" }).then(
    () => undefined,
    () => undefined
  );
}

export function clearSummarizeJobs(scope: SummarizeJob["scope"], ownerId: string): void {
  for (const job of listSummarizeJobs(scope, ownerId)) {
    controllers.get(job.key)?.abort();
    controllers.delete(job.key);
    jobs.delete(job.key);
    void deleteServerSummarize(job.serverJobId);
  }
  emit();
}

export function useSummarizeJobList(): SummarizeJob[] {
  const [snapshot, setSnapshot] = useState<SummarizeJob[]>(() => listSummarizeJobs());
  useEffect(() => subscribeSummarizeJobs(() => setSnapshot(listSummarizeJobs())), []);
  return snapshot;
}

function isAbortError(reason: unknown): boolean {
  return reason instanceof Error && reason.name === "AbortError";
}
